import { P2GButton, P2GCard, P2GProgress, P2GText } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { RoutingConstants } from '../../../lib/consts/RoutingConstants';
import useGoto from '../../../../../hooks/use-goto';
import useSubscriptionsEnabled from '~/hooks/use-subscriptions-enabled';
import { useEntitlements } from '~/hooks/use-entitlements';
import { resolveTierFromProductName } from '~/apps/subscriptions/resolve-tier';
import { isTopTier } from '~/apps/subscriptions/types';

export const PlanWidget = ({
  logEvent,
  vipContent
}: {
  logEvent: (link: string) => void;
  /** Rendered in place of the card for a VIP customer - the sidebar's VIP banner slot. */
  vipContent?: React.ReactNode;
}) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { goTo } = useGoto();
  const subscriptionsEnabled = useSubscriptionsEnabled();
  const { subscription, status, billingDetailUnavailable, entitlements } = useEntitlements();

  // A tenant with no subscriptions has nothing for this card to link to - hide it entirely.
  if (!subscriptionsEnabled) {
    return null;
  }

  // `vip_type` is a parameter-valued entitlement (the VIP tier level, e.g. '7'), not a boolean
  // 'true'/'false' flag - so presence, not equality, is what marks the customer VIP. This is the
  // gate between the VIP banner and this card, independent of which paid tier they're on.
  const isVip = entitlements.some((e) => !!e.values?.['vip_type']);

  if (isVip) {
    return vipContent ?? null;
  }

  /**
   * What the card should say. `null` renders the spinner, i.e. "we don't know yet, or can't say".
   *
   * Branch order matters. "Free" is only ever the answer to a settled, successful read that found
   * no subscription and no stale grant - never to a read that failed, and never to
   * `billingDetailUnavailable`, which means perks were granted but the billing provider was
   * unreachable. Telling a paying subscriber they are on Free is the defect this replaces.
   */
  const resolvePlanLabel = (): string | null => {
    if (status === 'loading' || status === 'unauthenticated') {
      return null;
    }

    // A failed read is silence, not an answer about what the customer is paying for.
    if (status === 'error') {
      return null;
    }

    if (billingDetailUnavailable) {
      return t('SIDEBAR_PLAN_WIDGET_UNAVAILABLE', { defaultValue: 'Unavailable' });
    }

    if (subscription) {
      // Tier names are Stripe product names, not localised copy - deliberately rendered as-is, the
      // same way the package's own private useAccountTier()/resolveAccountTier() badges the header
      // avatar. Entitlement `key`/`name` are opaque tier labels (e.g. 'prepay_bonus_t1') and must
      // never be branched on for this - see @parcel2go/npm-p2g-app's Entitlement type doc.
      const tier = resolveTierFromProductName(subscription.productName);
      if (tier) {
        return tier === 'pro' ? 'Pro' : 'Plus';
      }
      // A product this app doesn't recognise is still a plan the customer is paying for. Show
      // Stripe's own name for it rather than guessing, and certainly rather than saying "Free".
      return subscription.productName?.trim() || t('SIDEBAR_PLAN_WIDGET_UNKNOWN', { defaultValue: 'Your plan' });
    }

    return t('SIDEBAR_PLAN_WIDGET_VALUE', { defaultValue: 'Free' });
  };

  const planLabel = resolvePlanLabel();

  // `null` for Free (no subscription) or for a product this app doesn't recognise - neither is
  // evidence of where they sit in our tier ladder, so both fall back to the generic CTA below.
  const resolvedTier = subscription ? resolveTierFromProductName(subscription.productName) : null;

  /**
   * Whether there is anything above what they hold. Only `true` when we POSITIVELY know they are on
   * a tier below the top: a product we cannot identify, or perks with no billing detail behind them,
   * are not evidence an upgrade exists, and offering one we cannot stand behind sends the customer
   * to a page that will not have it. A customer with no subscription at all is genuinely below the
   * top, so they keep the upgrade framing.
   */
  const canUpgrade = (() => {
    if (status !== 'ready' || billingDetailUnavailable) {
      return false;
    }
    if (!subscription) {
      return true;
    }
    return resolvedTier !== null && !isTopTier(resolvedTier);
  })();

  const ctaLabel =
    resolvedTier === 'plus'
      ? t('SIDEBAR_PLAN_WIDGET_CTA_UPGRADE_PRO', { defaultValue: 'Upgrade to Pro' })
      : canUpgrade
        ? t('SIDEBAR_PLAN_WIDGET_CTA', { defaultValue: 'Upgrade Plan' })
        : t('SIDEBAR_PLAN_WIDGET_CTA_MANAGE', { defaultValue: 'Manage Plan' });

  return (
    <P2GCard
      variant="outlined"
      sx={{
        my: 2,
        mx: 'auto',
        textAlign: 'center',
        p: 2,
        // Without the fullWidth button, the card would shrink-to-fit the "Current Plan"/tier text,
        // which is narrower than this - a fixed width keeps every state the same size.
        width: '230px'
      }}
    >
      <P2GText variant="body2">{t('SIDEBAR_PLAN_WIDGET_TITLE', { defaultValue: 'Current Plan' })}</P2GText>
      {planLabel === null ? (
        <P2GProgress size="small" />
      ) : (
        <P2GText variant="subtitle2" sx={{ display: 'block' }}>
          {planLabel}
        </P2GText>
      )}
      <P2GButton
        variant="outlined"
        size="small"
        fullWidth
        onClick={() => {
          logEvent('my-plan');
          goTo(RoutingConstants.MyPlan);
        }}
        sx={{ my: 1 }}
      >
        {ctaLabel}
      </P2GButton>
      <P2GText variant="caption">
        {canUpgrade
          ? t('SIDEBAR_PLAN_WIDGET_CAPTION', { defaultValue: 'Unlock more features' })
          : t('SIDEBAR_PLAN_WIDGET_CAPTION_TOP', { defaultValue: 'View your plan and billing' })}
      </P2GText>
    </P2GCard>
  );
};
